import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'

export type SurveyQuestionType = 'RATING' | 'TEXT' | 'YES_NO'

export interface SurveyQuestion {
    id: number
    question_text: string
    question_type: SurveyQuestionType
    required: boolean
    order: number
}

export interface SurveyAnswer {
    question_id: number
    value: string
}

interface MoveInSurveyResponse {
    id: number
    lease: number
    rating: number
    comment: string
    created_date: string
}

export const useMoveInSurvey = () => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
    const [questions, setQuestions] = useState<SurveyQuestion[]>([])

    const fetchQuestions = useCallback(async (leaseId: number) => {
        setIsLoadingQuestions(true)
        try {
            const res = await axiosInstance.get<SurveyQuestion[]>(
                'survey/move-in-survey/questions/',
                { params: { lease_id: leaseId } },
            )
            setQuestions(res.data)
            setIsLoadingQuestions(false)
            return res.data
        } catch {
            setIsLoadingQuestions(false)
            setQuestions([])
            return []
        }
    }, [])

    const submitSurvey = useCallback(
        async (
            leaseId: number,
            rating: number,
            comment?: string,
            answers?: SurveyAnswer[],
        ) => {
            setIsSubmitting(true)
            try {
                const body: Record<string, unknown> = {
                    lease_id: leaseId,
                    rating,
                    comment: comment || '',
                }
                if (answers && answers.length > 0) {
                    body.answers = answers
                }
                const res = await axiosInstance.post<MoveInSurveyResponse>(
                    'survey/move-in-survey/submit/',
                    body,
                )
                setIsSubmitting(false)
                return res.data
            } catch (e: any) {
                setIsSubmitting(false)
                if (e?.response?.status === 409) {
                    return undefined
                }
                const detail = e?.response?.data?.detail
                if (detail) {
                    toast.error(detail)
                } else {
                    toast.error('Error submitting survey')
                }
                return Promise.reject(e)
            }
        },
        [],
    )

    return { isSubmitting, isLoadingQuestions, questions, fetchQuestions, submitSurvey }
}
